import { useEffect, useRef, useState } from "react";

export const useAudioVisualizer = (isActive, localStream, remoteAudioRef) => {
    const [micLevel, setMicLevel] = useState(0);
    const [speakerLevel, setSpeakerLevel] = useState(0);

    const micAnalyserRef = useRef(null);
    const micAudioContextRef = useRef(null);
    const micAnimationRef = useRef(null);

    const speakerAnalyserRef = useRef(null);
    const speakerAudioCtxRef = useRef(null);
    const speakerAnimationRef = useRef(null);

    useEffect(() => {
        // Mic Analyser
        if (isActive && localStream) {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;

            // Check if localStream tracks are active
            const source = audioCtx.createMediaStreamSource(localStream);
            source.connect(analyser);
            micAnalyserRef.current = analyser;
            micAudioContextRef.current = audioCtx;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const updateMicLevel = () => {
                analyser.getByteTimeDomainData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const v = (dataArray[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / dataArray.length);
                setMicLevel(rms);
                micAnimationRef.current = requestAnimationFrame(updateMicLevel);
            };
            updateMicLevel();
        }

        // Speaker Analyser
        let speakerCheckInterval;
        const attachSpeaker = async () => {
            // Check if remote audio stream has tracks
            if (remoteAudioRef.current &&
                remoteAudioRef.current.srcObject &&
                remoteAudioRef.current.srcObject.getAudioTracks().length > 0 &&
                !speakerAnalyserRef.current) {

                console.log("Attaching speaker analyzer to original stream...");
                try {
                    let audioCtx = speakerAudioCtxRef.current;
                    if (!audioCtx) {
                        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                        speakerAudioCtxRef.current = audioCtx;
                    }
                    if (audioCtx.state === 'suspended') {
                        try { await audioCtx.resume(); } catch (e) { console.warn("Resume failed", e); }
                    }

                    const source = audioCtx.createMediaStreamSource(remoteAudioRef.current.srcObject);
                    const analyser = audioCtx.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);

                    speakerAnalyserRef.current = analyser;
                    speakerAudioCtxRef.current = audioCtx; // Ensure ref is updated

                    const dataArray = new Uint8Array(analyser.frequencyBinCount);

                    const updateSpeakerLevel = () => {
                        if (!speakerAnalyserRef.current) return;
                        analyser.getByteFrequencyData(dataArray);

                        let sum = 0;
                        for (let i = 0; i < dataArray.length; i++) {
                            sum += dataArray[i];
                        }

                        // Average level (0-255)
                        const avg = sum / dataArray.length;

                        // Normalize to 0-1
                        let val = avg / 50; // Sensitivity boost

                        setSpeakerLevel(Math.min(val, 1));
                        speakerAnimationRef.current = requestAnimationFrame(updateSpeakerLevel);
                    };
                    updateSpeakerLevel();
                    console.log("Speaker analyzer attached successfully");
                    return true;
                } catch (e) {
                    console.error("Error attaching speaker analyzer", e);
                }
            }
            return false;
        };

        if (isActive) {
            if (speakerCheckInterval) clearInterval(speakerCheckInterval);
            speakerCheckInterval = setInterval(async () => {
                const attached = await attachSpeaker();
                if (attached) {
                    clearInterval(speakerCheckInterval);
                }
            }, 1000);
        }

        return () => {
            // Cleanup Mic
            if (micAnimationRef.current) cancelAnimationFrame(micAnimationRef.current);
            if (micAudioContextRef.current) micAudioContextRef.current.close();
            micAnalyserRef.current = null;
            micAudioContextRef.current = null;
            setMicLevel(0);

            // Cleanup Speaker
            if (speakerCheckInterval) clearInterval(speakerCheckInterval);
            if (speakerAnimationRef.current) cancelAnimationFrame(speakerAnimationRef.current);
            if (speakerAudioCtxRef.current && speakerAudioCtxRef.current.state !== 'closed') {
                // We might reuse context, so maybe don't close? 
                // But if we create new one on next call, we should close.
                // Logic in parent re-creates context on handleCall? 
                // It assigns new context on handleCall.
                // So we can close it here.
                speakerAudioCtxRef.current.close();
            }
            speakerAnalyserRef.current = null;
            speakerAudioCtxRef.current = null;
            setSpeakerLevel(0);
        };
    }, [isActive, localStream, remoteAudioRef]);

    return { micLevel, speakerLevel, speakerAudioCtxRef };
};
